import "./index.css";
// src/ProductSelector/ProductSelector.tsx
import * as React2 from "react";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button2 from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import SpaceBetween from "@cloudscape-design/components/space-between";
import TextContent from "@cloudscape-design/components/text-content";
import { useAppTranslation as useAppTranslation2 } from "@amzn/awsmp-max-translations";

// src/ProductSearch/ProductSearch.tsx
import * as React from "react";
import Select from "@cloudscape-design/components/select";
import { useAppTranslation } from "@amzn/awsmp-max-translations";
import { jsx } from "react/jsx-runtime";
var LANE_LABEL_KEYS = /* @__PURE__ */ new Map([
  ["SOFTWARE", "productLaneSoftware"],
  ["PROFESSIONAL_SERVICES", "productLaneProfessionalServices"],
  ["MULTI_PRODUCT", "productLaneMultiProduct"],
  ["AI_AGENTS", "productLaneAiAgents"]
]);
var ProductSearch = ({
  maxService,
  onSelect,
  placeholder = "Search for AWS Marketplace products...",
  disabled = false,
  maxResults = 5,
  onError,
  recommendedProducts,
  otherConsideredProducts,
  selectedProductIds = []
}) => {
  const { t } = useAppTranslation();
  const [selectedOption, setSelectedOption] = React.useState(null);
  const [_filteringText, setFilteringText] = React.useState("");
  const [searchResultOptions, setSearchResultOptions] = React.useState([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const searchTimeoutRef = React.useRef();
  const latestRequestIdRef = React.useRef(0);
  const hasGrouping = Boolean(recommendedProducts || otherConsideredProducts);
  const productToOption = React.useCallback(
    (product) => ({
      value: product.productId,
      label: product.productName,
      description: `by ${product.vendorName}`,
      __product: product
    }),
    []
  );
  const options = React.useMemo(() => {
    if (!hasGrouping) {
      return searchResultOptions;
    }
    const groups = [];
    const availableRecommended = (recommendedProducts || []).filter((p) => !selectedProductIds.includes(p.productId));
    const availableOther = (otherConsideredProducts || []).filter((p) => !selectedProductIds.includes(p.productId));
    const allGroupedIds = /* @__PURE__ */ new Set([
      ...(recommendedProducts || []).map((p) => p.productId),
      ...(otherConsideredProducts || []).map((p) => p.productId)
    ]);
    const availableSearchResults = searchResultOptions.filter(
      (opt) => !selectedProductIds.includes(opt.value || "") && !allGroupedIds.has(opt.value || "")
    );
    const hasLanes = [...recommendedProducts || [], ...otherConsideredProducts || []].some((p) => p.pathType);
    const otherLaneOptions = [];
    if (hasLanes) {
      const lanes = /* @__PURE__ */ new Map();
      for (const product of [...availableRecommended, ...availableOther]) {
        const labelKey = LANE_LABEL_KEYS.get(product.pathType || "");
        const lane = labelKey ? lanes.get(labelKey) : otherLaneOptions;
        if (lane)
          lane.push(productToOption(product));
        else if (labelKey)
          lanes.set(labelKey, [productToOption(product)]);
      }
      lanes.forEach((laneOptions, labelKey) => groups.push({ label: t(labelKey), options: laneOptions }));
    }
    if (!hasLanes && availableRecommended.length > 0) {
      groups.push({
        label: "Recommended products",
        options: availableRecommended.map(productToOption)
      });
    }
    if (!hasLanes && availableOther.length > 0) {
      groups.push({
        label: "Other products considered",
        options: availableOther.map(productToOption)
      });
    }
    const otherOptions = [...otherLaneOptions, ...availableSearchResults];
    if (otherOptions.length > 0) {
      groups.push({
        label: "Other",
        options: otherOptions
      });
    }
    return groups;
  }, [hasGrouping, recommendedProducts, otherConsideredProducts, selectedProductIds, searchResultOptions, productToOption, t]);
  const searchProducts = React.useCallback(
    async (query) => {
      if (!query.trim()) {
        setSearchResultOptions([]);
        return;
      }
      const requestId = ++latestRequestIdRef.current;
      setIsSearching(true);
      try {
        const result = await maxService.searchListings({
          searchText: query,
          maxResults,
          includeInsights: false
        });
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
        if (result.success && result.output && typeof result.output === "object" && "output" in result.output) {
          const typedOutput = result.output;
          if (typedOutput.output.searchListings.ListingSummaries) {
            const selectOptions = typedOutput.output.searchListings.ListingSummaries.map((listing) => {
              const logo = listing.Media?.LOGO_THUMBNAIL;
              const logoUrl = logo?.DefaultDomain && logo?.Path ? `${logo.DefaultDomain}${logo.Path}` : void 0;
              const product = {
                productId: listing.Id || "",
                productName: listing.DisplayAttributes?.Title || "Unknown Product",
                vendorName: listing.ProductAttributes?.Creator?.DisplayName || "Unknown Vendor",
                productLogoUrl: logoUrl,
                badges: listing.Badges?.map((b) => ({
                  name: b.DisplayName || "",
                  value: b.Value
                }))
              };
              return {
                value: product.productId,
                label: product.productName,
                description: `by ${product.vendorName}`,
                // Store full product in option for selection
                __product: product
              };
            });
            setSearchResultOptions(selectOptions);
          } else {
            setSearchResultOptions([]);
          }
        } else {
          setSearchResultOptions([]);
        }
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
        console.error("Search failed:", err);
        const errorMessage = err instanceof Error ? err.message : "Failed to search products. Please try again.";
        onError?.(errorMessage);
        setSearchResultOptions([]);
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setIsSearching(false);
        }
      }
    },
    [maxService, maxResults, onError]
  );
  const handleFilteringChange = (filteringText) => {
    setFilteringText(filteringText);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (filteringText.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        searchProducts(filteringText);
      }, 500);
    } else {
      setSearchResultOptions([]);
    }
  };
  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);
  const handleChange = (option) => {
    if (option && option.__product) {
      const product = option.__product;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      onSelect(product);
      setSelectedOption(null);
      setFilteringText("");
      setSearchResultOptions([]);
    }
  };
  return /* @__PURE__ */ jsx(
    Select,
    {
      selectedOption,
      onChange: ({ detail }) => handleChange(detail.selectedOption),
      options,
      filteringType: "manual",
      onLoadItems: ({ detail }) => handleFilteringChange(detail.filteringText),
      statusType: isSearching ? "loading" : "finished",
      placeholder,
      empty: "Type to search for products",
      loadingText: "Searching products...",
      disabled,
      virtualScroll: true
    }
  );
};

// src/ProductToken/ProductToken.tsx
import Button from "@cloudscape-design/components/button";
import Link from "@cloudscape-design/components/link";
import { ListingBadge } from "@amzn/apo-ux-patterns/components/ListingBadge";

// src/ProductToken/ProductToken.module.scss
var ProductToken_module_default = {
  token: "ProductToken_module_token",
  tokenClickable: "ProductToken_module_tokenClickable",
  tokenIcon: "ProductToken_module_tokenIcon",
  tokenContent: "ProductToken_module_tokenContent",
  tokenLabel: "ProductToken_module_tokenLabel",
  externalLinkIcon: "ProductToken_module_externalLinkIcon",
  tokenDescription: "ProductToken_module_tokenDescription",
  tokenBadges: "ProductToken_module_tokenBadges",
  tokenDismiss: "ProductToken_module_tokenDismiss"
};

// src/ProductToken/ProductToken.tsx
import { Fragment, jsx as jsx2, jsxs } from "react/jsx-runtime";
var ProductToken = ({ product, onDismiss, productUrl }) => {
  const tokenContent = /* @__PURE__ */ jsxs(Fragment, { children: [
    product.productLogoUrl && /* @__PURE__ */ jsx2("img", { src: product.productLogoUrl, alt: "", className: ProductToken_module_default.tokenIcon }),
    /* @__PURE__ */ jsxs("div", { className: ProductToken_module_default.tokenContent, children: [
      /* @__PURE__ */ jsx2("div", { className: ProductToken_module_default.tokenLabel, children: productUrl ? /* @__PURE__ */ jsx2(Link, { external: true, href: productUrl, fontSize: "inherit", children: product.productName }) : product.productName }),
      /* @__PURE__ */ jsxs("div", { className: ProductToken_module_default.tokenDescription, children: [
        "by ",
        product.vendorName
      ] }),
      product.badges && product.badges.length > 0 && /* @__PURE__ */ jsx2("div", { className: ProductToken_module_default.tokenBadges, children: product.badges.map((badge) => badge.value && /* @__PURE__ */ jsx2(ListingBadge, { variant: badge.value }, badge.value)) })
    ] }),
    onDismiss && /* @__PURE__ */ jsx2(Button, { variant: "icon", iconName: "close", ariaLabel: `Remove ${product.productName}`, onClick: onDismiss, className: ProductToken_module_default.tokenDismiss })
  ] });
  return /* @__PURE__ */ jsx2("div", { className: ProductToken_module_default.token, children: tokenContent });
};

// src/ProductSelector/ProductSelector.module.scss
var ProductSelector_module_default = {
  tokenContainer: "ProductSelector_module_tokenContainer"
};

// src/ProductSelector/ProductSelector.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var ProductSelector = ({
  maxService,
  initialProducts = [],
  onChange,
  onConfirm,
  onCancel,
  disabled = false,
  onError,
  recommendedProducts,
  otherConsideredProducts
}) => {
  const { t } = useAppTranslation2();
  const [selectedProducts, setSelectedProducts] = React2.useState(initialProducts);
  const [error, setError] = React2.useState(null);
  const selectedProductIds = React2.useMemo(() => selectedProducts.map((p) => p.productId), [selectedProducts]);
  React2.useEffect(() => {
    onChange?.(initialProducts);
  }, []);
  const handleProductSelect = (product) => {
    if (selectedProducts.some((p) => p.productId === product.productId)) {
      setError(t("productAlreadySelected"));
      return;
    }
    if (selectedProducts.length >= 5) {
      setError(t("maximumProductsToCompare", { count: 5 }));
      return;
    }
    const newProducts = [...selectedProducts, product];
    setSelectedProducts(newProducts);
    setError(null);
    onChange?.(newProducts);
  };
  const handleRemoveProduct = (productId) => {
    const newProducts = selectedProducts.filter((p) => p.productId !== productId);
    setSelectedProducts(newProducts);
    setError(null);
    onChange?.(newProducts);
  };
  const handleConfirm = () => {
    if (selectedProducts.length < 2) {
      setError(t("minimumProductsToCompare", { count: 2 }));
      return;
    }
    onConfirm(selectedProducts);
  };
  const isConfirmDisabled = selectedProducts.length < 2 || selectedProducts.length > 5 || disabled;
  return /* @__PURE__ */ jsxs2(SpaceBetween, { size: "m", children: [
    error && /* @__PURE__ */ jsx3(Alert, { type: "error", dismissible: true, onDismiss: () => setError(null), children: error }),
    /* @__PURE__ */ jsxs2(SpaceBetween, { size: "m", children: [
      /* @__PURE__ */ jsx3(FormField, { label: t("productsLabel"), children: /* @__PURE__ */ jsx3(
        ProductSearch,
        {
          maxService,
          onSelect: handleProductSelect,
          disabled: selectedProducts.length >= 5,
          placeholder: t("enterProductNamePlaceholder"),
          onError,
          recommendedProducts,
          otherConsideredProducts,
          selectedProductIds
        }
      ) }),
      selectedProducts.length > 0 && /* @__PURE__ */ jsxs2(SpaceBetween, { size: "m", children: [
        /* @__PURE__ */ jsx3("div", { className: ProductSelector_module_default.tokenContainer, children: selectedProducts.map((product) => /* @__PURE__ */ jsx3(ProductToken, { product, onDismiss: () => handleRemoveProduct(product.productId) }, product.productId)) }),
        /* @__PURE__ */ jsx3(TextContent, { children: /* @__PURE__ */ jsx3(Box, { variant: "span", color: "text-body-secondary", children: t("selectUpToNProducts", { count: 5 }) }) }),
        /* @__PURE__ */ jsxs2("div", { style: { display: "flex", justifyContent: "flex-end", gap: "8px" }, children: [
          onCancel && /* @__PURE__ */ jsx3(Button2, { variant: "link", onClick: onCancel, children: t("cancel") }),
          /* @__PURE__ */ jsx3(Button2, { variant: "primary", onClick: handleConfirm, disabled: isConfirmDisabled, children: t("compareProducts") })
        ] })
      ] })
    ] })
  ] });
};
export {
  ProductSelector
};
//# sourceMappingURL=index.js.map
